/**
 * ActiveUsersWidget - Виджет активных пользователей
 * ==================================================
 * 
 * Показывает графики DAU/WAU/MAU и их лояльные версии (LDAU/LWAU/LMAU)
 * LDAU = пользователи, у которых первая активность была ДО текущего периода
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Calendar, 
  BarChart3, 
  LineChart as LineChartIcon, 
  AreaChart as AreaChartIcon 
} from 'lucide-react';
import { users as usersSpecs } from '@ggstat/chart-specs';

// Ряды по группировке (dau/ldau, wau/lwau, mau/lmau) — из shared/chart-specs
const SERIES_BY_GROUP = usersSpecs.activeUsers.byGroup;

const COLORS = Object.fromEntries(
  Object.values(SERIES_BY_GROUP).flat().map((s) => [s.id, s.color])
);

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle, trend }) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold">{MetricsService.formatNumber(value)}</p>
              {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          <div className="flex flex-col items-end">
            {getTrendIcon()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Кастомная легенда с поддержкой включения/выключения
 */
function CustomLegend({ enabledLines, setEnabledLines, groupBy }) {
  // Определяем какие метрики показывать в зависимости от группировки
  const getMetrics = () => {
    const series = SERIES_BY_GROUP[groupBy] || SERIES_BY_GROUP.day;
    return Object.fromEntries(series.map((s) => [s.id, { color: s.color, label: s.label }]));
  };
  
  const metrics = getMetrics();
  
  return (
    <div className="flex justify-center gap-6 mt-4">
      {Object.entries(metrics).map(([key, { color, label }]) => (
        <div
          key={key}
          onClick={() => setEnabledLines(prev => ({ ...prev, [key]: !prev[key] }))}
          className="flex items-center gap-2 cursor-pointer select-none"
          style={{ opacity: enabledLines[key] ? 1 : 0.4 }}
        >
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: color }}
          />
          <span className="text-sm">{label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Компонент графика активных пользователей
 */
function ActiveUsersChart({ data, chartType = 'line', enabledLines, setEnabledLines, groupBy }) {
  // Определяем ключи данных в зависимости от группировки
  const getDataKeys = () => {
    switch (groupBy) {
      case 'week':
        return { active: 'activeUsers', loyal: 'loyalActiveUsers', activeKey: 'wau', loyalKey: 'lwau' };
      case 'month':
        return { active: 'activeUsers', loyal: 'loyalActiveUsers', activeKey: 'mau', loyalKey: 'lmau' };
      default:
        return { active: 'activeUsers', loyal: 'loyalActiveUsers', activeKey: 'dau', loyalKey: 'ldau' };
    }
  };
  
  const { active, loyal, activeKey, loyalKey } = getDataKeys();
  
  // Получаем настройки оси X
  const xAxisConfig = getXAxisConfig(groupBy, data.length);
  
  const CustomTooltip = ({ active: isActive, payload, label }) => {
    if (isActive && payload && payload.length > 0) {
      const activeUsers = payload.find(p => p.dataKey === 'activeUsers')?.value || 0;
      const loyalUsers = payload.find(p => p.dataKey === 'loyalActiveUsers')?.value || 0;
      const newUsers = activeUsers - loyalUsers;
      
      // Форматируем дату для tooltip
      const formattedLabel = formatTooltipDate(label, groupBy);
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {MetricsService.formatNumber(entry.value)}
            </p>
          ))}
          <div className="border-t mt-2 pt-2">
            <p className="text-sm text-muted-foreground">
              New Users: {MetricsService.formatNumber(newUsers)} ({activeUsers > 0 ? ((newUsers / activeUsers) * 100).toFixed(1) : 0}%)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  const getActiveLabel = () => {
    switch (groupBy) {
      case 'week': return 'WAU';
      case 'month': return 'MAU';
      default: return 'DAU';
    }
  };
  
  const getLoyalLabel = () => {
    switch (groupBy) {
      case 'week': return 'LWAU';
      case 'month': return 'LMAU';
      default: return 'LDAU';
    }
  };
  
  if (chartType === 'bar') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => MetricsService.formatNumber(value)}
            />
            <Tooltip order="series" content={<CustomTooltip />} />
            {enabledLines[activeKey] && (
              <Bar 
                dataKey={active} 
                name={getActiveLabel()}
                fill={COLORS[activeKey]}
                isAnimationActive={false}
              />
            )}
            {enabledLines[loyalKey] && (
              <Bar 
                dataKey={loyal} 
                name={getLoyalLabel()}
                fill={COLORS[loyalKey]}
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
        <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} groupBy={groupBy} />
      </div>
    );
  }
  
  if (chartType === 'area') {
    return (
      <div>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period"
              tickFormatter={(value) => formatChartDate(value, groupBy)}
              {...xAxisConfig}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => MetricsService.formatNumber(value)}
            />
            <Tooltip content={<CustomTooltip />} />
            {enabledLines[activeKey] && (
              <Area 
                type="monotone" 
                dataKey={active} 
                name={getActiveLabel()}
                stroke={COLORS[activeKey]}
                fill={COLORS[activeKey]}
                fillOpacity={0.3}
                isAnimationActive={false}
              />
            )}
            {enabledLines[loyalKey] && (
              <Area 
                type="monotone" 
                dataKey={loyal} 
                name={getLoyalLabel()}
                stroke={COLORS[loyalKey]}
                fill={COLORS[loyalKey]}
                fillOpacity={0.3}
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} groupBy={groupBy} />
      </div>
    );
  }
  
  // Default: line chart
  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatNumber(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {enabledLines[activeKey] && (
            <Line 
              type="monotone" 
              dataKey={active} 
              name={getActiveLabel()}
              stroke={COLORS[activeKey]}
              strokeWidth={2}
              dot={{ fill: COLORS[activeKey], r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
          {enabledLines[loyalKey] && (
            <Line 
              type="monotone" 
              dataKey={loyal} 
              name={getLoyalLabel()}
              stroke={COLORS[loyalKey]}
              strokeWidth={2}
              dot={{ fill: COLORS[loyalKey], r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      <CustomLegend enabledLines={enabledLines} setEnabledLines={setEnabledLines} groupBy={groupBy} />
    </div>
  );
}

/**
 * Основной компонент виджета
 */
export function ActiveUsersWidget() {
  // Используем localStorage для сохранения настроек
  const [groupBy, setGroupBy] = useLocalStorage('active-users-widget-groupBy', 'day');
  const [chartType, setChartType] = useLocalStorage('active-users-widget-chartType', 'line');
  const [enabledLines, setEnabledLines] = useLocalStorage('active-users-widget-enabledLines', {
    dau: true,
    ldau: true,
    wau: true,
    lwau: true,
    mau: true,
    lmau: true
  });
  
  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      // MetricsService.getActiveUsers уже возвращает { total, data, summary, groupBy, filters }
      // благодаря ApiService который извлекает data из { success, data }
      const response = await MetricsService.getActiveUsers(filters, groupBy, options);
      return response;
    },
    [groupBy]
  );
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  
  // widgetData.data содержит { total, data, summary, groupBy, filters }
  // Но useWidgetData возвращает это как data напрямую
  const activeUsersData = widgetProps.data;
  
  // Получаем название метрики
  const getMetricName = () => {
    switch (groupBy) {
      case 'week': return 'WAU';
      case 'month': return 'MAU';
      default: return 'DAU';
    }
  };
  
  // Компоненты выбора настроек
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily">
          <Calendar className="h-4 w-4 mr-1" />
          DAU
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly">
          <Calendar className="h-4 w-4 mr-1" />
          WAU
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly">
          <Calendar className="h-4 w-4 mr-1" />
          MAU
        </ToggleGroupItem>
      </ToggleGroup>
      
      <div className="h-6 w-px bg-border" />
      
      <ToggleGroup type="single" value={chartType} onValueChange={(value) => value && setChartType(value)}>
        <ToggleGroupItem value="line" aria-label="Line Chart">
          <LineChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart">
          <BarChart3 className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart">
          <AreaChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
  
  return (
    <BaseWidget
      {...widgetProps}
      title="Active Users Analytics"
      subtitle={activeUsersData?.summary ? `Avg ${getMetricName()}: ${MetricsService.formatNumber(activeUsersData.summary.average)}` : null}
      headerAction={<Controls />}
      emptyMessage="No active users data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        {/* Статистика */}
        {activeUsersData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label={`Average ${getMetricName()}`}
              value={activeUsersData.summary.average}
              trend={activeUsersData.summary.trend}
            />
            <StatCard
              icon={TrendingUp}
              label={`Max ${getMetricName()}`}
              value={activeUsersData.summary.max}
            />
            <StatCard
              icon={TrendingDown}
              label={`Min ${getMetricName()}`}
              value={activeUsersData.summary.min}
            />
            <StatCard
              icon={Users}
              label="Total Periods"
              value={activeUsersData.data?.length || 0}
              subtitle={`${groupBy === 'day' ? 'days' : groupBy === 'week' ? 'weeks' : 'months'}`}
            />
          </div>
        )}
        
        {/* График */}
        {activeUsersData?.data && activeUsersData.data.length > 0 && (
          <ActiveUsersChart 
            data={activeUsersData.data} 
            chartType={chartType}
            enabledLines={enabledLines}
            setEnabledLines={setEnabledLines}
            groupBy={groupBy}
          />
        )}
      </div>
    </BaseWidget>
  );
}

export default ActiveUsersWidget;
